import _ from 'lodash';
import {
  Product,
  Additional,
  Scheduling,
  ScheduleTime,
  Tariff,
  TariffGroup,
  DateAndTimeScheduleQuantity,
  SellingMode,
  AdditionalType,
  FeaturedTariffPreviewMode,
} from '@src/core/buyerApiClient';
import { TariffsPerType, TariffsState } from '@src/features/product';
import { schedulingContainsMinute } from './schedulings';
import { formatCents } from '.';

export const mountTariffsPerTypeListFromTariffGroup = (
  tariffGroup: TariffGroup,
  minSaleTariffQuantities: number,
): TariffsPerType[] => {
  return tariffGroup.tariffs.map((tariff) => ({
    type: tariff.type,
    selectedQuantity: minSaleTariffQuantities,
    schedulings: [],
    tariffGroup,
  }));
};

export const mountTariffsPerTypeListFromSchedulings = (
  schedulings: Scheduling[],
  minSaleTariffQuantities: number,
): TariffsPerType[] => {
  const tariffTypeMap: TariffsPerType[] = [];

  for (const sch of schedulings) {
    for (const tariff of sch.tariffGroup.tariffs) {
      const foundTariff = tariffTypeMap.find((t) => t.type.id === tariff.type.id);

      if (foundTariff) {
        foundTariff.schedulings.push(sch);
      } else {
        tariffTypeMap.push({
          type: tariff.type,
          selectedQuantity: minSaleTariffQuantities,
          schedulings: [sch],
        });
      }
    }
  }

  return tariffTypeMap;
};

export const getCorrectTariffFromScheduling = (tariffsPerType: TariffsPerType, scheduling: Scheduling) =>
  scheduling.tariffGroup.tariffs.find((t) => t.type.id === tariffsPerType.type.id) ?? null;

export const scheduleIsAvailable = (tariffsInformation: TariffsState, scheduleMinutes: number): boolean => {
  const { listOfTariffsPerType } = tariffsInformation;

  // Check if schedule is available for every selected tariff
  const onlySelectedTariffsPerType = listOfTariffsPerType.filter((tt) => tt.selectedQuantity > 0);
  const availableForAll = onlySelectedTariffsPerType.every((tt) =>
    tt.schedulings.some((sch) => schedulingContainsMinute(sch, scheduleMinutes)),
  );

  if (!availableForAll) {
    return false;
  }

  const schedulingContainingMinute = listOfTariffsPerType.reduce<Scheduling | null>(
    (curr, tt) => curr ?? tt.schedulings.find((sch) => schedulingContainsMinute(sch, scheduleMinutes)) ?? null,
    null,
  );

  if (schedulingContainingMinute) {
    // Check if tariff max occupation for scheduling is respected
    for (const tariffsPerType of listOfTariffsPerType) {
      const tariff = getCorrectTariffFromScheduling(tariffsPerType, schedulingContainingMinute);

      if (tariff) {
        const tariffOccupationInfo = schedulingContainingMinute.occupationRates.tariffScheduleQuantities?.find(
          (toi) => toi.tariffId === tariff.id,
        );
        const scheduleOccupationInfo = tariffOccupationInfo!.occupationSchedule?.find(
          (sq) => sq.minutes === scheduleMinutes,
        );
        const maxQuantityForMinute = scheduleOccupationInfo?.occupation.availableAmount ?? null;

        if (maxQuantityForMinute != null && maxQuantityForMinute < tariffsPerType.selectedQuantity) {
          return false;
        }
      }
    }

    // Check if general max occupation for scheduling is respected
    // if tariff is available, general occupation can be null or 0
    const sumAllQuantities = listOfTariffsPerType.reduce((sum, t) => sum + t.selectedQuantity, 0);
    const scheduleOccupationInfo = schedulingContainingMinute.occupationRates.scheduleQuantities?.find(
      (sq) => sq.minutes === scheduleMinutes,
    );
    const maxQuantityForMinute = scheduleOccupationInfo?.occupation.availableAmount ?? null;

    if (maxQuantityForMinute != null && maxQuantityForMinute !== 0 && maxQuantityForMinute < sumAllQuantities) {
      return false;
    }
  }

  return true;
};

export const tariffIsAvailable = ({ sellingMode }: Product, tariffsPerType: TariffsPerType): boolean => {
  if (sellingMode === SellingMode.WithoutDate) {
    return true;
  }

  for (const scheduling of tariffsPerType.schedulings) {
    const tariff = getCorrectTariffFromScheduling(tariffsPerType, scheduling)!;

    switch (sellingMode) {
      case SellingMode.WithDateOnly:
        const tariffQuantities = scheduling.occupationRates.tariffQuantities?.find((toi) => toi.tariffId === tariff.id);
        const maxQuantity = tariffQuantities?.occupation.availableAmount;

        if (maxQuantity === null || maxQuantity! > 0) {
          return true;
        }

        break;

      case SellingMode.WithDateAndTime:
        const tariffOccupationInfo = scheduling.occupationRates.tariffScheduleQuantities?.find(
          (toi) => toi.tariffId === tariff.id,
        );

        if (tariffOccupationInfo?.occupationSchedule) {
          for (const sq of tariffOccupationInfo.occupationSchedule) {
            const quantity = sq.occupation.availableAmount;
            if (quantity === null || quantity > 0) {
              return true;
            }
          }
        }

        break;
    }
  }

  return false;
};

export const extractLimitFrom = (
  tariffsInformation: TariffsState,
  tariffsPerType: TariffsPerType,
  hourSelected?: number,
): [number, number] | null => {
  const { listOfTariffsPerType } = tariffsInformation;
  const quantityOfSelectedTariffs = listOfTariffsPerType.reduce(
    (value, tariff) => value + (tariff.selectedQuantity ?? 0),
    0,
  );
  const shouldUseSchedules = tariffsPerType.schedulings.some((sch) => sch.availableScheduleTimes);

  if (hourSelected != null && shouldUseSchedules) {
    const scheduling = tariffsPerType.schedulings.find((sch) => schedulingContainsMinute(sch, hourSelected));
    if (scheduling) {
      const tariff = getCorrectTariffFromScheduling(tariffsPerType, scheduling);

      if (tariff) {
        const generalOccupation = scheduling.occupationRates.scheduleQuantities!.find(
          (sq) => sq.minutes === hourSelected,
        );
        const tariffScheduleQuantities = scheduling.occupationRates.tariffScheduleQuantities!.find(
          (tsq) => tsq.tariffId === tariff.id,
        );
        const tariffOccupation = tariffScheduleQuantities!.occupationSchedule.find((sq) => sq.minutes === hourSelected);

        return generateTariffRange(
          tariffsPerType.selectedQuantity,
          quantityOfSelectedTariffs,
          generalOccupation!.occupation.availableAmount,
          tariffOccupation!.occupation.availableAmount,
        );
      }
    }
  }

  if (shouldUseSchedules) {
    const ranges = tariffsPerType.schedulings
      .filter((scheduling) => scheduling.availableScheduleTimes)
      .map((scheduling) => {
        const tariff = getCorrectTariffFromScheduling(tariffsPerType, scheduling);

        const generalQuantity = getMaxQuantityFromScheduleQuantities(
          scheduling.occupationRates.scheduleQuantities!,
          hourSelected,
        );
        const tariffScheduleQuantities = scheduling.occupationRates.tariffScheduleQuantities!.find(
          (tsq) => tsq.tariffId === tariff!.id,
        );
        const tariffQuantity = getMaxQuantityFromScheduleQuantities(
          tariffScheduleQuantities!.occupationSchedule,
          hourSelected,
        );

        return generateTariffRange(
          tariffsPerType.selectedQuantity,
          quantityOfSelectedTariffs,
          generalQuantity,
          tariffQuantity,
        );
      });

    const minValueRange = ranges.reduce((range1, range2) => {
      if (!range1) {
        return range2;
      }
      if (!range2) {
        return range1;
      }
      return range1![1] < range2![1] ? range1 : range2;
    });

    return minValueRange;
  }

  for (const scheduling of tariffsPerType.schedulings) {
    const tariff = getCorrectTariffFromScheduling(tariffsPerType, scheduling);
    const tariffQuantity = scheduling.occupationRates.tariffQuantities?.find((tsq) => tsq.tariffId === tariff!.id);

    return generateTariffRange(
      tariffsPerType.selectedQuantity,
      quantityOfSelectedTariffs,
      scheduling.occupationRates.quantity?.availableAmount,
      tariffQuantity!.occupation.availableAmount,
    );
  }

  return null;
};

const getMaxQuantityFromScheduleQuantities = (
  scheduleQuantities: DateAndTimeScheduleQuantity[],
  hoursSelected?: number,
) => {
  if (hoursSelected != null) {
    const minuteOccupation = scheduleQuantities.find((sq) => sq.minutes === hoursSelected);
    return minuteOccupation?.occupation.availableAmount;
  }

  const unlimited = scheduleQuantities.some((sq) => sq.occupation.availableAmount === null);
  if (unlimited) {
    return null;
  }

  return scheduleQuantities.reduce((value, sq) => Math.max(value, sq.occupation.availableAmount ?? 0), 0);
};

const generateTariffRange = (
  tariffSelectedQuantity?: number,
  othersSelectedQuantity?: number,
  globalLimit?: number | null,
  tariffLimit?: number | null,
): [number, number] | null => {
  const hasGlobalLimit = globalLimit !== null;
  const hasTariffLimit = tariffLimit !== null;
  const globalRemaining = (globalLimit ?? 0) - (othersSelectedQuantity ?? 0) + (tariffSelectedQuantity ?? 0);
  if (!hasGlobalLimit && !hasTariffLimit) {
    return null;
  }
  if (hasGlobalLimit && !hasTariffLimit) {
    return [0, globalRemaining];
  }
  if (hasTariffLimit && !hasGlobalLimit) {
    return [0, tariffLimit ?? 0];
  }

  if (tariffLimit === undefined) {
    return null;
  }

  const minimumValue = Math.min(globalRemaining, tariffLimit ?? 0);
  return [0, minimumValue];
};

export const isTariffQtdSelectedAvailable = (listOfTariffsPerType: TariffsPerType[]): boolean => {
  const quantitySum = listOfTariffsPerType.reduce((acc, tariffPerType) => acc + tariffPerType.selectedQuantity, 0);

  for (const tariffsPerType of listOfTariffsPerType) {
    for (const scheduling of tariffsPerType.schedulings) {
      // Check tariff occupation first
      const tariff = getCorrectTariffFromScheduling(tariffsPerType, scheduling);
      if (tariff && scheduling.occupationRates.tariffQuantities) {
        const tariffQuantity = scheduling.occupationRates.tariffQuantities.find((tq) => tq.tariffId === tariff.id)!;
        if (
          tariffQuantity.occupation.availableAmount != null &&
          tariffsPerType.selectedQuantity > tariffQuantity.occupation.availableAmount
        ) {
          return false;
        }
      }

      // If tariff is available, general occupation can be null or 0
      if (scheduling.occupationRates.quantity) {
        if (
          scheduling.occupationRates.quantity.availableAmount != null &&
          scheduling.occupationRates.quantity.availableAmount != 0 &&
          quantitySum > scheduling.occupationRates.quantity.availableAmount
        ) {
          return false;
        }
      }
    }
  }

  return true;
};

export const getAdditionalPrice = (
  additional: Additional,
  tariffs: TariffsPerType[],
  additionalSelectedQuantity?: number,
) => {
  switch (additional.type) {
    case AdditionalType.Item:
      return additional.priceCents;

    case AdditionalType.Quantity:
      return (additionalSelectedQuantity ?? 0) * additional.priceCents;

    case AdditionalType.Person:
    case AdditionalType.PersonWithInput:
      return tariffs
        .filter((tariff) => tariff.selectedQuantity)
        .reduce((next, tariff) => {
          const exempted = additional.exemptedTariffTypes ?? [];
          const tariffIsExempted = exempted.some(({ tariffTypeId }) => String(tariffTypeId) === String(tariff.type.id));
          return tariffIsExempted ? next : next + (tariff.selectedQuantity ?? 0) * additional.priceCents;
        }, 0);
  }
};

export const getTariffPriceInformation = (
  tariffsPerType: TariffsPerType,
  selectedHour: ScheduleTime | null,
): number => {
  const { selectedQuantity: quantity } = tariffsPerType;

  if (tariffsPerType.schedulings.length > 0) {
    const tariffPrice = getTariffPriceForScheduleTime(tariffsPerType, selectedHour);
    if (tariffPrice) {
      return tariffPrice.priceCents * quantity;
    }

    return 0;
  }

  if (tariffsPerType.tariffGroup) {
    const tariff = tariffsPerType.tariffGroup.tariffs.find((t) => t.type.id === tariffsPerType.type.id);
    return tariff!.priceCents * quantity;
  }

  return 0;
};

export interface ITariffPrice {
  priceCents: number;
  oldPriceCents: number | null;
}

export const getTariffPriceForScheduleTime = (
  tariffsPerType: TariffsPerType,
  selectedHour: ScheduleTime | null,
): ITariffPrice | null => {
  for (const scheduling of tariffsPerType.schedulings) {
    if (!selectedHour || schedulingContainsMinute(scheduling, selectedHour.minutes)) {
      const tariff = getCorrectTariffFromScheduling(tariffsPerType, scheduling);
      if (!tariff) {
        continue;
      }

      const schedulePrice = scheduling.tariffScheduleUnitPrices.find(
        (tsup) => !selectedHour || tsup.time === selectedHour.formattedTime,
      );
      const tariffSchedulePrice = schedulePrice?.tariffs.find((t) => t.tariffId === tariff.id);
      if (!tariffSchedulePrice) {
        continue;
      }

      return {
        priceCents: tariffSchedulePrice?.priceCents ?? tariff.priceCents,
        oldPriceCents: tariff.oldPriceCents ?? null,
      };
    }
  }

  return null;
};

export const getSchedulingsTariffPreview = (
  schedulings: Scheduling[],
  mode: FeaturedTariffPreviewMode,
): number | null => {
  const prices = schedulings.reduce((acc, sch) => {
    const desiredTariffs = sch.tariffGroup.tariffs.filter(
      (tariff) => !tariff.isDependent && (mode !== FeaturedTariffPreviewMode.Default || tariff.isDefault),
    );

    const priceValues = sch.tariffScheduleUnitPrices.reduce((priceAcc, price) => {
      const tariffs = price.tariffs.filter((t) => desiredTariffs.some((dt) => dt.id === t.tariffId));
      return [...priceAcc, ...tariffs.map((t) => t.priceCents)];
    }, [] as number[]);

    return [...acc, ...priceValues];
  }, [] as number[]);

  if (prices.length === 0) {
    return null;
  }

  switch (mode) {
    case FeaturedTariffPreviewMode.Highest:
      return Math.max(...prices);
    default:
      return Math.min(...prices);
  }
};

export const getSelectedTariff = (tariffPerType: TariffsPerType, selectedMinutes: number | null): Tariff | null => {
  for (const scheduling of tariffPerType.schedulings) {
    if (selectedMinutes != null) {
      if (schedulingContainsMinute(scheduling, selectedMinutes)) {
        return getCorrectTariffFromScheduling(tariffPerType, scheduling);
      }
    } else {
      return getCorrectTariffFromScheduling(tariffPerType, scheduling);
    }
  }

  if (tariffPerType.tariffGroup) {
    return tariffPerType.tariffGroup.tariffs.find((t) => t.type.id === tariffPerType.type.id) ?? null;
  }

  return null;
};

export interface SelectedTariff {
  tariff: Tariff;
  quantity: number;
  tariffsPerType: TariffsPerType;
}

export const makeSelectedTariffs = (
  tariffsPerTypeSelected: TariffsPerType[],
  selectedMinutes: number | null,
): SelectedTariff[] => {
  const selectedTariffs: SelectedTariff[] = [];
  for (const t of tariffsPerTypeSelected) {
    const tariff = getSelectedTariff(t, selectedMinutes);
    if (tariff) {
      const quantity = t.selectedQuantity;
      selectedTariffs.push({ tariff, quantity, tariffsPerType: t });
    }
  }

  return selectedTariffs;
};

export const tariffDepedencyStatus = (
  listOfTariffsPerType: TariffsPerType[],
  selectedHour: ScheduleTime | undefined,
) => {
  let areSomeIndependentTariffSelected = false;
  let areSomeDependentTariffSelected = false;

  listOfTariffsPerType.forEach((tariffPerType) => {
    const tariff = getSelectedTariff(tariffPerType, selectedHour ? selectedHour.minutes : null);
    const isSelected = tariff && tariffPerType.selectedQuantity > 0;

    if (isSelected && !tariff.isDependent) {
      areSomeIndependentTariffSelected = true;
    }
    if (isSelected && tariff.isDependent) {
      areSomeDependentTariffSelected = true;
    }
  });

  return {
    areSomeIndependentTariffSelected,
    hasTariffDependencyError: !areSomeIndependentTariffSelected && areSomeDependentTariffSelected,
  };
};

interface SplitHourListProps {
  hour: ScheduleTime;
  error: boolean;
}
export const splitHourList = (hoursList: SplitHourListProps[]): [SplitHourListProps[], SplitHourListProps[]] => {
  const middleNight = hoursList.find(({ hour }) => hour.formattedTime === '00:00');

  const morningHours = hoursList?.filter(
    ({ hour }) => hour.formattedTime !== middleNight?.hour.formattedTime && +hour.formattedTime.split(':')[0]! < 18,
  );
  const lateHours = hoursList?.filter(
    ({ hour }) => hour.formattedTime !== middleNight?.hour.formattedTime && +hour.formattedTime.split(':')[0]! >= 18,
  );

  if (middleNight) {
    lateHours.push(middleNight!);
  }

  return [morningHours, lateHours];
};

export const tariffPriceWithDiscount = (price: string, discount: number) => {
  if (price.includes(' - ')) {
    const [minPrice, maxPrice] = price.split(' - ');
    const minPriceCents = parseInt(minPrice!.replace(/\D/g, '') || '0');
    const maxPriceCents = parseInt(maxPrice!.replace(/\D/g, '') || '0');

    const minPriceCentsWithDIscount = Math.max(Math.floor(minPriceCents - minPriceCents * discount), 0);
    const maxPriceCentsWithDiscount = Math.max(Math.floor(maxPriceCents - maxPriceCents * discount), 0);

    return `${formatCents(minPriceCentsWithDIscount)} - ${formatCents(maxPriceCentsWithDiscount)}`;
  }

  const priceCents = parseInt(price.replace(/\D/g, '') || '0');
  const priceCentsWithDiscount = Math.max(Math.floor(priceCents - priceCents * discount), 0);

  return formatCents(priceCentsWithDiscount);
};
